// The masterclass page worked out from its rows: how far they've watched, where Play starts, and
// what shows where the offer goes. Pure: no network, no React, nothing but format.js, so a test can
// import it as it is (tests/e2e/masterclass.spec.js).
import * as f from '../../lib/format.js'
import { COPY } from './copy.js'

// the columns of their own member_product_state row that say whether play and offer were sent (set
// only by masterclass-event, readable by the member): data.js reads them, the tests replay them
export const SENT_COLUMNS = 'product_id, play_sent_at, offer_sent_at'

// Only the video at /masterclass ("Buy your next property without a deposit") sends its play and offer
// events to n8n: n8n's masterclass-watch workflows (Masterclass Watch Events, YsFerSJVe2yfH28M) serve
// only that video, and masterclass-event finds it by this slug. Another masterclass made in admin (the
// product-types plan, 13 Oct) sends nothing, so its viewers never start the video's follow-up emails;
// follow-ups of its own would take a new n8n workflow Adam switches on, and a small hub change.
export const WATCH_EVENTS_SLUG = 'masterclass'
export const sendsWatchEvents = product => product?.slug === WATCH_EVENTS_SLUG

// Where Play carries on from, as the shared player's startFrom has it (src/product/Player.jsx):
// where they left off, if that's more than 10 seconds in and not at the very end; else the top
export function resumeAt(position, duration) {
  const s = Math.max(0, Math.floor(Number(position) || 0))
  if (s > 10 && (!duration || s < duration - 5)) return s
  return 0
}

// How far they've watched, from the furthest point they've reached (as the Library's tile counts
// it, src/library/model.js), with this visit's furthest point on top: a full bar once the database
// has marked it watched, and never 100% before that.
//   row: their recording_progress row or null; live: { position, furthest } this visit, or null
export function progressView({ row = null, duration = 0, live = null } = {}) {
  const furthest = Math.max(row?.furthest_seconds || 0, live?.furthest || 0)
  const watched = Boolean(row?.watched_at)
  let pct = 0
  if (watched) pct = 100
  else if (duration > 0) pct = Math.min(99, Math.round((Math.min(furthest, duration) / duration) * 100))
  // while it plays, where it is now; otherwise where Play would carry on from
  const position = live?.position != null ? live.position : row?.position_seconds || 0
  const at = live?.position != null ? (live.position > 10 ? live.position : 0) : resumeAt(position, duration)
  return {
    pct,
    pctLabel: `${pct}%`,
    watchedLine: COPY.watched(pct),
    resume: resumeAt(row?.position_seconds || 0, duration),
    carryOn: at > 0 ? COPY.carryOn(f.clock(at)) : COPY.start,
    // a place to carry on from, so "Start from the beginning" means something (member review, 11 Oct)
    canRestart: at > 0,
  }
}

// Their first visit: nowhere to carry on from yet (the lede says so, COPY.ledeFirst), by the same 10-second
// rule as Play and Start from the beginning (resumeAt), so the line and the buttons agree. Anyone marked
// watched, or who has been past the first 10 seconds, keeps the board's line: a finished viewer's saved
// position is 0 (the player reports 0 at the end), so the position alone can't say.
export function firstVisit(row, duration = 0) {
  return !row?.watched_at && resumeAt(row?.position_seconds || 0, duration) === 0 && (row?.furthest_seconds || 0) <= 10
}

// The cover's name for the button: where Play carries on from, or the masterclass from the top
export function coverLabel(resume) {
  return resume > 0 ? COPY.coverCarryOn(f.clock(resume)) : COPY.coverPlay
}

// The minute the offer appears, from the owner's setting (5700, 1:35:00), or null for none: then it
// appears only at the end of the video or on "Show it now"
export function revealSeconds(offer) {
  const s = offer?.at_seconds
  return Number.isInteger(s) && s >= 0 ? s : null
}

// What shows where the offer goes, from hub.my_offer_state's answer (docs/data-model.md 10.2). It
// fails closed: nothing until the database has answered, and nothing when it errs or gives no row.
// - card: the owner's card (POYT open to them now, or paid in full on the old course site), from the
//   start, as the board's "ownsPOYT Yes" draws it;
// - placeholder, then offer: the offer is on and they own no POYT, before and after it's reached;
// - none: the offer is off, or they hold POYT in any other way (a secured place included), or the
//   answer isn't in.
//   status: 'loading' | 'ready' | 'error'; answer: the row or null; reached: whether it's been reached
export function offerArea({ status, answer, reached }) {
  if (status !== 'ready' || !answer) return { kind: 'none' }
  if (answer.owner_card && answer.card && typeof answer.card === 'object') return { kind: 'card', card: answer.card }
  if (!answer.offer_hidden && answer.offer && typeof answer.offer === 'object') {
    return { kind: reached ? 'offer' : 'placeholder', offer: answer.offer }
  }
  return { kind: 'none' }
}

// The price box's lines, as the board draws settings.offer.price_lines: the old price struck
// through, the public price, the price itself large with what follows its first space smaller
// ("£997 +vat" is "£997" and "+vat"), then the rest. An empty line is left out.
export function priceLines(lines) {
  const list = Array.isArray(lines) ? lines.map(l => (typeof l === 'string' ? l.trim() : '')) : []
  const [was = '', publicPrice = '', price = '', ...rest] = list
  const gap = price.indexOf(' ')
  return {
    was,
    publicPrice,
    big: gap > 0 ? price.slice(0, gap) : price,
    small: gap > 0 ? price.slice(gap + 1).trim() : '',
    notes: rest.filter(Boolean),
  }
}

// The heading with its last sentence underlined, as the board underlines "One yes." in "Two offers.
// One yes."; a heading of 1 sentence has no underline
export function headingParts(heading) {
  const text = typeof heading === 'string' ? heading.trim() : ''
  const m = text.match(/^(.*[.?!:]\s+)(\S[^]*)$/)
  if (!m) return { lead: text, marked: '' }
  return { lead: m[1], marked: m[2] }
}

// A link the owner's card or the offer may open: a page in the hub (/poyt) or a whole https://
// address (course.adamslee.co.uk until POYT moves in; a Stripe payment link). Anything else draws
// no button rather than a dead link.
export function linkKind(url) {
  if (typeof url !== 'string') return null
  if (/^\/[^/\\\s][^\s\\]*$/.test(url) || url === '/') return 'hub'
  if (/^https:\/\/[^\s]+$/.test(url)) return 'web'
  return null
}
